import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";
import { Reveal, Eyebrow } from "../components/Shared";
import { BUSINESS, PACKAGES, SERVICES, UPGRADES } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
  "w-full rounded-xl border border-white/10 bg-coal px-4 py-3 text-sm text-white placeholder:text-muted focus:outline-none focus:border-bright focus:ring-1 focus:ring-bright transition-colors";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-mono uppercase tracking-widest text-steel">{label}</span>
    {children}
  </label>
);

function Form({ mode }) {
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [upgrades, setUpgrades] = useState([]);
  const [pkg, setPkg] = useState("");
  const booking = mode === "booking";

  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const data = Object.fromEntries(fd.entries());
    if (booking) data.upgrades = upgrades.join(", ");
    setSending(true);
    try {
      const res = await axios.post(booking ? `${API}/book` : `${API}/quote`, data);
      if (res.data.status === "ok") {
        setDone(true);
        toast.success(booking ? "Booking request sent — we'll confirm shortly." : "Quote request sent — we'll be in touch soon.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please call us instead.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div data-testid={`${mode}-success`} className="rounded-2xl border border-bright/40 bg-bright/5 p-10 text-center">
        <CheckCircle2 size={44} className="mx-auto text-bright" />
        <h3 className="mt-4 font-display text-2xl font-bold">
          {booking ? "Booking request received" : "Quote request received"}
        </h3>
        <p className="mt-3 text-sm text-steel">
          We've sent it straight to our team. Prefer to talk now? Call{" "}
          <a href={BUSINESS.phoneHref} className="text-bright font-medium">{BUSINESS.phone}</a>.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`${mode}-form`} className="rounded-2xl border border-white/10 bg-card p-7 sm:p-9 space-y-4">
      <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Name">
          <input required name="name" data-testid={`${mode}-name-input`} className={inputCls} placeholder="Your name" maxLength={100} />
        </Field>
        <Field label="Phone">
          <input required name="phone" type="tel" data-testid={`${mode}-phone-input`} className={inputCls} placeholder="(605) 000-0000" maxLength={25} />
        </Field>
      </div>
      <Field label="Email">
        <input required name="email" type="email" data-testid={`${mode}-email-input`} className={inputCls} placeholder="you@email.com" maxLength={120} />
      </Field>
      <Field label="Vehicle (Year / Make / Model)">
        <input required name="vehicle" data-testid={`${mode}-vehicle-input`} className={inputCls} placeholder="e.g. 2021 BMW X5" maxLength={120} />
      </Field>
      <div className="grid sm:grid-cols-2 gap-4">
        {booking ? (
          <>
            <Field label="Package">
              <select required name="package" data-testid="booking-package-select" className={inputCls} value={pkg} onChange={(e) => setPkg(e.target.value)}>
                <option value="" disabled>Select a package</option>
                {PACKAGES.map((p) => (
                  <option key={p.name} value={p.name}>{p.name} — from ${p.price}</option>
                ))}
              </select>
            </Field>
            <Field label="Service Type">
              <select required name="mode" data-testid="booking-mode-select" className={inputCls} defaultValue="in-shop">
                <option value="in-shop">In-shop at our studio</option>
                <option value="mobile">Mobile — at my location</option>
              </select>
            </Field>
          </>
        ) : (
          <>
            <Field label="Service">
              <select required name="service" data-testid="quote-service-select" className={inputCls} defaultValue="">
                <option value="" disabled>Select a service</option>
                {SERVICES.map((s) => (
                  <option key={s.path} value={s.name}>{s.name}</option>
                ))}
                <option value="Ceramic Coating">Ceramic Coating</option>
                <option value="Other / Not sure">Other / Not sure</option>
              </select>
            </Field>
            <Field label="Preferred Date (optional)">
              <input name="preferred_date" type="date" data-testid="quote-date-input" className={inputCls} />
            </Field>
          </>
        )}
      </div>
      {booking ? (
        <Field label="Preferred Date (optional)">
          <input name="preferred_date" type="date" data-testid="booking-date-input" className={inputCls} />
        </Field>
      ) : null}
      {booking ? (
        <div>
          <span className="mb-1.5 block text-xs font-mono uppercase tracking-widest text-steel">Upgrades (optional)</span>
          <div className="space-y-2" data-testid="booking-upgrades">
            {UPGRADES.map((u, i) => {
              const on = upgrades.includes(u.name);
              return (
                <label
                  key={u.name}
                  data-testid={`booking-upgrade-${i}`}
                  className={`flex items-center justify-between gap-4 rounded-xl border px-4 py-3 cursor-pointer transition-colors ${on ? "border-bright/60 bg-bright/10" : "border-white/10 bg-coal hover:border-white/25"}`}
                >
                  <span className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-[#C2261B]"
                      checked={on}
                      onChange={() => setUpgrades(on ? upgrades.filter((x) => x !== u.name) : [...upgrades, u.name])}
                    />
                    <span className="text-sm text-white">{u.name}</span>
                  </span>
                  <span className="font-mono text-sm text-bright shrink-0">+${u.price}</span>
                </label>
              );
            })}
          </div>
        </div>
      ) : null}
      {booking ? (
        <div data-testid="booking-total" className="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-card px-5 py-4">
          <span className="text-sm text-steel">
            Estimated starting total
            <span className="block text-[11px] text-muted mt-0.5">Starting price — final quote depends on your vehicle's size</span>
          </span>
          <span className="font-display text-2xl font-extrabold text-white shrink-0">
            ${(((PACKAGES.find((p) => p.name === pkg) || {}).price || 0) + upgrades.reduce((s, u) => s + (UPGRADES.find((x) => x.name === u) || {}).price, 0)).toLocaleString("en-US")}
          </span>
        </div>
      ) : null}
      <Field label={booking ? "Notes (condition, pet hair, stains…)" : "Tell us about your vehicle & goals"}>
        <textarea name="notes" rows={4} data-testid={`${mode}-notes-input`} className={inputCls} maxLength={1000} placeholder={booking ? "Anything we should know before we start" : "What would you like done?"} />
      </Field>
      <button
        type="submit"
        disabled={sending}
        data-testid={`${mode}-submit-button`}
        className="w-full flex items-center justify-center gap-2 rounded-full bg-bright hover:bg-accent disabled:opacity-60 text-white font-semibold py-3.5 transition-colors"
      >
        {sending ? <Loader2 size={17} className="animate-spin" /> : null}
        {sending ? "Sending…" : booking ? "Request Booking" : "Request Free Quote"}
      </button>
      <p className="text-center text-xs text-muted">
        Or call us directly:{" "}
        <a href={BUSINESS.phoneHref} data-testid={`${mode}-phone-link`} className="text-bright font-medium">{BUSINESS.phone}</a>
      </p>
    </form>
  );
}

export default function Book() {
  const [tab, setTab] = useState("booking");
  return (
    <>
      <Seo
        title="Book a Detail or Free Quote | A+ Detailing Sioux Falls, SD"
        description="Book your interior detail, full package or ceramic coating online, or request a free quote for paint correction, PPF and window tinting in Sioux Falls, SD."
        path="/book"
      />
      <section className="mx-auto max-w-4xl px-5 sm:px-8 pt-36 pb-24 sm:pt-44 sm:pb-28">
        <Reveal><Eyebrow>Book & Free Quotes</Eyebrow></Reveal>
        <Reveal delay={0.08}>
          <h1 className="mt-4 font-display text-4xl sm:text-6xl font-extrabold uppercase tracking-tight leading-[0.98]">
            Reserve your<br />spot.
          </h1>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-steel leading-relaxed">
            Requests go straight to our team at {BUSINESS.email} — we confirm your
            appointment by phone or email. Mobile & in-shop across Sioux Falls.
          </p>
        </Reveal>

        <Reveal delay={0.24}>
          <div className="mt-10 inline-flex glass border border-white/10 rounded-full p-1.5" data-testid="book-tabs">
            {[
              { id: "booking", label: "Book a Detail" },
              { id: "quote", label: "Free Quote" },
            ].map((t) => (
              <button
                key={t.id}
                data-testid={`book-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`relative rounded-full px-6 py-2.5 text-sm font-semibold transition-colors ${tab === t.id ? "text-white" : "text-steel hover:text-white"}`}
              >
                {tab === t.id && (
                  <motion.span layoutId="tab-pill" className="absolute inset-0 rounded-full bg-bright" transition={{ duration: 0.3 }} />
                )}
                <span className="relative z-10">{t.label}</span>
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.3 }}
            >
              {tab === "booking" ? <Form key="b" mode="booking" /> : <Form key="q" mode="quote" />}
            </motion.div>
          </AnimatePresence>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-10 flex items-center justify-center gap-2 text-sm text-muted">
            <Phone size={14} className="text-bright" /> In-shop at {BUSINESS.address} · Mobile across Sioux Falls
          </div>
        </Reveal>
      </section>
    </>
  );
}
